import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Link2, Loader2, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";

export const publicShareUrl = (slug) => `${window.location.origin}/api/t/${slug}`;

export const PublicShareControl = ({ tripId }) => {
  const [state, setState] = useState({ is_public: false, share_slug: null, loading: true });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/trips/${tripId}/public-share`)
      .then((r) => setState({ ...r.data, loading: false }))
      .catch(() => setState((s) => ({ ...s, loading: false })));
  }, [tripId]);

  const url = state.share_slug ? publicShareUrl(state.share_slug) : "";

  const copy = async (u) => {
    try {
      await navigator.clipboard.writeText(u);
      toast.success("Public link copied — paste it anywhere for a preview card");
    } catch {
      toast.success(`Public link: ${u}`);
    }
  };

  const toggle = async (enabled) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${tripId}/public-share`, { enabled });
      setState({ ...data, loading: false });
      if (enabled && data.share_slug) { track("trip_shared", { method: "public_link" }); await copy(publicShareUrl(data.share_slug)); }
      else if (!enabled) toast.success("Public link turned off");
    } catch {
      toast.error("Couldn't update public sharing.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-5 pt-5 border-t border-black/10" data-testid="public-share-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-medium text-sm inline-flex items-center gap-1.5"><Link2 className="w-4 h-4 text-[#C8553D]" /> Public read-only link</p>
          <p className="text-xs text-muted-foreground mt-0.5">Shows a rich preview card in WhatsApp, iMessage, Slack and LinkedIn. Read-only, no account needed.</p>
        </div>
        <button
          role="switch"
          aria-checked={state.is_public}
          disabled={busy || state.loading}
          onClick={() => toggle(!state.is_public)}
          data-testid="public-share-toggle"
          className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${state.is_public ? "bg-[#1A3B2B]" : "bg-black/20"} disabled:opacity-50`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${state.is_public ? "translate-x-5" : ""}`} />
          {busy && <Loader2 className="absolute inset-0 m-auto w-3 h-3 animate-spin text-white" />}
        </button>
      </div>
      {state.is_public && url && (
        <div className="mt-3">
          <div className="flex items-center gap-2 p-2 rounded-full border border-black/10 bg-white">
            <input readOnly value={url} data-testid="public-share-url" className="flex-1 min-w-0 bg-transparent text-sm px-2 outline-none" />
            <a href={url} target="_blank" rel="noreferrer" className="p-1.5 rounded-full hover:bg-black/5" title="Open" data-testid="public-share-open"><ExternalLink className="w-4 h-4" /></a>
            <button onClick={() => copy(url)} data-testid="public-share-copy" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1A3B2B] text-[#FDFBF7] text-sm shrink-0"><Copy className="w-3.5 h-3.5" /> Copy</button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">Turn the switch off to disable the link — it will stop working immediately.</p>
        </div>
      )}
    </div>
  );
};
